import React, { useState, useCallback, useRef, useEffect } from "react";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { toast } from "react-toastify";
import { GoogleMap, LoadScript, Marker, Autocomplete } from "@react-google-maps/api";

const mapContainerStyle = {
    width: "100%",
    height: "400px",
    borderRadius: "8px",
};

const defaultCenter = {
    lat: 24.7136,
    lng: 46.6753,
};

const libraries = ["places"];

const LocationPicker = ({ latitude, longitude, onSave }) => {
    const [lat, setLat] = useState(latitude || "");
    const [lng, setLng] = useState(longitude || "");
    const [saving, setSaving] = useState(false);
    const [markerPosition, setMarkerPosition] = useState(latitude && longitude ? { lat: parseFloat(latitude), lng: parseFloat(longitude) } : null);
    const [mapCenter, setMapCenter] = useState(latitude && longitude ? { lat: parseFloat(latitude), lng: parseFloat(longitude) } : defaultCenter);
    const [isLoadingLocation, setIsLoadingLocation] = useState(false);
    const [searchAddress, setSearchAddress] = useState("");
    const [isSearching, setIsSearching] = useState(false);
    const [isMapLoaded, setIsMapLoaded] = useState(false);

    const mapRef = useRef(null);
    const autocompleteRef = useRef(null);
    const hasSetInitialLocation = useRef(false);

    // Get current location on mount
    useEffect(() => {
        if (latitude && longitude) {
            const position = {
                lat: parseFloat(latitude),
                lng: parseFloat(longitude),
            };
            setMarkerPosition(position);
            setMapCenter(position);
            setLat(latitude);
            setLng(longitude);
            hasSetInitialLocation.current = true;
        } else if (!hasSetInitialLocation.current) {
            getCurrentLocation();
        }
    }, [latitude, longitude]);

    const getCurrentLocation = () => {
        if ("geolocation" in navigator) {
            setIsLoadingLocation(true);

            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const newLat = position.coords.latitude;
                    const newLng = position.coords.longitude;
                    const newPosition = { lat: newLat, lng: newLng };

                    setMarkerPosition(newPosition);
                    setMapCenter(newPosition);
                    setLat(newLat.toFixed(6));
                    setLng(newLng.toFixed(6));

                    hasSetInitialLocation.current = true;
                    setIsLoadingLocation(false);

                    console.log("✅ Current location detected:", newPosition);
                },
                (error) => {
                    console.error("❌ Geolocation error:", error);

                    let errorMessage = "Unable to get current location";

                    switch (error.code) {
                        case error.PERMISSION_DENIED:
                            errorMessage = "Location access denied. Please enable location permissions.";
                            break;
                        case error.POSITION_UNAVAILABLE:
                            errorMessage = "Location information unavailable";
                            break;
                        case error.TIMEOUT:
                            errorMessage = "Location request timeout";
                            break;
                        default:
                            errorMessage = "Unknown location error";
                    }

                    toast.warn(errorMessage);
                    setMapCenter(defaultCenter);
                    setIsLoadingLocation(false);
                },
                {
                    enableHighAccuracy: true,
                    timeout: 10000,
                    maximumAge: 0,
                },
            );
        } else {
            toast.error("Geolocation is not supported by your browser");
            setMapCenter(defaultCenter);
        }
    };

    const onMapClick = useCallback((e) => {
        const newLat = e.latLng.lat();
        const newLng = e.latLng.lng();

        setMarkerPosition({ lat: newLat, lng: newLng });
        setLat(newLat.toFixed(6));
        setLng(newLng.toFixed(6));
    }, []);

    const onMarkerDragEnd = useCallback((e) => {
        const newLat = e.latLng.lat();
        const newLng = e.latLng.lng();

        setMarkerPosition({ lat: newLat, lng: newLng });
        setLat(newLat.toFixed(6));
        setLng(newLng.toFixed(6));
    }, []);

    const handleSave = async () => {
        if (!lat || !lng) {
            toast.error("Please set a location on the map");
            return;
        }

        setSaving(true);
        try {
            await onSave(parseFloat(lat), parseFloat(lng));
            toast.success("Location saved successfully");
        } catch (error) {
            toast.error("Failed to save location");
        } finally {
            setSaving(false);
        }
    };

    const handleGetCurrentLocation = () => {
        getCurrentLocation();
    };

    const onAutocompleteLoad = (autocomplete) => {
        autocompleteRef.current = autocomplete;
    };

    const onPlaceChanged = () => {
        if (autocompleteRef.current) {
            const place = autocompleteRef.current.getPlace();

            if (place.geometry && place.geometry.location) {
                const newLat = place.geometry.location.lat();
                const newLng = place.geometry.location.lng();
                const newPosition = { lat: newLat, lng: newLng };

                setMarkerPosition(newPosition);
                setMapCenter(newPosition);
                setLat(newLat.toFixed(6));
                setLng(newLng.toFixed(6));

                // Zoom to the location
                if (mapRef.current) {
                    mapRef.current.panTo(newPosition);
                    mapRef.current.setZoom(17);
                }

                toast.success("Location found successfully");
                console.log("✅ Address location found:", newPosition);
            } else {
                toast.error("No location found for this address");
            }
        }
    };

    const handleFindLocation = async () => {
        if (!searchAddress.trim()) {
            toast.warn("Please enter an address to search");
            return;
        }

        if (!window.google || !window.google.maps) {
            toast.error("Google Maps is not loaded yet. Please wait.");
            return;
        }

        setIsSearching(true);

        try {
            const geocoder = new window.google.maps.Geocoder();

            geocoder.geocode({ address: searchAddress }, (results, status) => {
                setIsSearching(false);

                if (status === "OK" && results[0]) {
                    const location = results[0].geometry.location;
                    const newLat = location.lat();
                    const newLng = location.lng();
                    const newPosition = { lat: newLat, lng: newLng };

                    setMarkerPosition(newPosition);
                    setMapCenter(newPosition);
                    setLat(newLat.toFixed(6));
                    setLng(newLng.toFixed(6));

                    // Zoom to the location
                    if (mapRef.current) {
                        mapRef.current.panTo(newPosition);
                        mapRef.current.setZoom(17);
                    }

                    toast.success("Location found successfully");
                    console.log("✅ Address location found:", newPosition);
                } else {
                    toast.error("Location not found. Please try a different address.");
                    console.error("Geocode error:", status);
                }
            });
        } catch (error) {
            setIsSearching(false);
            console.error("Error finding location:", error);
            toast.error("Failed to find location");
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === "Enter") {
            handleFindLocation();
        }
    };

    const onMapLoad = useCallback((map) => {
        mapRef.current = map;
        setIsMapLoaded(true);
        console.log("✅ Map loaded successfully");
    }, []);

    const onLoadScriptLoad = useCallback(() => {
        console.log("✅ Google Maps script loaded");
    }, []);

    const onLoadScriptError = useCallback((error) => {
        console.error("❌ Error loading Google Maps:", error);
        toast.error("Failed to load Google Maps");
    }, []);

    return (
        <div className="location-picker">
            <div className="flex justify-content-between align-items-center mb-3">
                <h3 className="m-0">Customer Location</h3>
                <Button label="Use Current Location" icon={isLoadingLocation ? "pi pi-spin pi-spinner" : "pi pi-map-marker"} onClick={handleGetCurrentLocation} className="p-button-outlined p-button-sm" disabled={isLoadingLocation} />
            </div>

            {isLoadingLocation && (
                <div className="flex align-items-center justify-content-center mb-2" style={{ padding: "10px", background: "#f0f9ff", borderRadius: "6px" }}>
                    <i className="pi pi-spin pi-spinner mr-2" style={{ color: "#3b82f6" }}></i>
                    <span style={{ color: "#1e40af", fontSize: "14px", fontWeight: 500 }}>Getting your location...</span>
                </div>
            )}

            {/* Single LoadScript wrapper for everything */}
            <LoadScript
                googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
                libraries={libraries}
                onLoad={onLoadScriptLoad}
                onError={onLoadScriptError}
                loadingElement={
                    <div className="flex align-items-center justify-content-center" style={{ padding: "20px" }}>
                        <i className="pi pi-spin pi-spinner mr-2" style={{ fontSize: "2rem", color: "#6366f1" }}></i>
                        <span style={{ color: "#475569", fontSize: "16px", fontWeight: 500 }}>Loading map...</span>
                    </div>
                }
            >
                {/* Address Search */}
                <div className="mb-3">
                    <label htmlFor="searchAddress" className="font-semibold block mb-2">
                        <i className="pi pi-search mr-2"></i>
                        Search by Address
                    </label>
                    <div className="p-inputgroup">
                        <Autocomplete
                            onLoad={onAutocompleteLoad}
                            onPlaceChanged={onPlaceChanged}
                            options={{
                                componentRestrictions: { country: [] },
                            }}
                        >
                            <InputText id="searchAddress" value={searchAddress} onChange={(e) => setSearchAddress(e.target.value)} onKeyPress={handleKeyPress} placeholder="Enter address (e.g., 123 Main St, City)" className="w-full" />
                        </Autocomplete>
                        <Button label="Find" icon={isSearching ? "pi pi-spin pi-spinner" : "pi pi-search"} onClick={handleFindLocation} disabled={isSearching || !searchAddress.trim() || !isMapLoaded} className="p-button-primary" />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                        <i className="pi pi-info-circle mr-1"></i>
                        Start typing to see suggestions, or click "Find" to search
                    </p>
                </div>

                {/* Map */}
                <div className="mb-3">
                    <GoogleMap
                        mapContainerStyle={mapContainerStyle}
                        center={mapCenter}
                        zoom={15}
                        onClick={onMapClick}
                        onLoad={onMapLoad}
                        options={{
                            streetViewControl: false,
                            mapTypeControl: true,
                            fullscreenControl: true,
                            zoomControl: true,
                        }}
                    >
                        {markerPosition && isMapLoaded && <Marker position={markerPosition} draggable={true} onDragEnd={onMarkerDragEnd} animation={window.google?.maps?.Animation?.DROP} />}
                    </GoogleMap>
                    <p className="text-sm text-gray-600 mt-2">
                        <i className="pi pi-info-circle mr-2"></i>
                        Click on the map or drag the red pin to set the exact location
                    </p>
                </div>
            </LoadScript>

            <div className="grid">
                <div className="col-12 md:col-6">
                    <div className="field">
                        <label htmlFor="latitude" className="font-semibold">
                            Latitude
                        </label>
                        <InputText id="latitude" value={lat} onChange={(e) => setLat(e.target.value)} placeholder="e.g., 24.7136" className="w-full" disabled />
                    </div>
                </div>
                <div className="col-12 md:col-6">
                    <div className="field">
                        <label htmlFor="longitude" className="font-semibold">
                            Longitude
                        </label>
                        <InputText id="longitude" value={lng} onChange={(e) => setLng(e.target.value)} placeholder="e.g., 46.6753" className="w-full" disabled />
                    </div>
                </div>
            </div>

            <Button label="Save Location" icon="pi pi-save" onClick={handleSave} loading={saving} className="w-full p-button-success" disabled={!lat || !lng} />
        </div>
    );
};

export default LocationPicker;
